import React, { useState, useEffect } from "react";
import { Card, CardBody, Container, Row, Col } from "reactstrap";
import { useNavigate } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Select from "react-select";
import ApiService from "../util/ApiService";
import Swal from "sweetalert2";
import LoadingScreen from "../util/LoadingScreen";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

require("./assets/css/Video.css");

const VideoQueuePage = () => {
    document.title = "Fila de Atendimento | Plantão Rápido";

    const navigate = useNavigate();
    const [loadingPage, setLoadingPage] = useState(false);
    const [pendingAttendances, setPendingAttendances] = useState([]);
    const [myActiveAttendances, setMyActiveAttendances] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dashboardStats, setDashboardStats] = useState({
        in_queue: 0,
        in_progress: 0,
        completed_today: 0,
        avg_wait_time: 0
    });

    useEffect(() => {
        fetchDepartments();
        fetchQueue();
        fetchMyActive();
        fetchDashboard();

        // Auto-refresh every 5 seconds
        const interval = setInterval(() => {
            fetchQueue(true);
            fetchMyActive(true);
        }, 5000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        fetchQueue();
    }, [selectedDepartment]);

    const fetchDepartments = async () => {
        try {
            // Using video department endpoint
            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/departments/list-department`,
                { per_page: 100, status: 1 },
                true
            );

            if (response.code === 200) {
                const { data } = ApiService.handleApiResponse(response);
                setDepartments(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Erro ao carregar departamentos:', error);
        }
    };

    const fetchDashboard = async () => {
        try {
            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/stats/dashboard`,
                {},
                true
            );

            if (response.code === 200 && response.data) {
                setDashboardStats(response.data);
            }
        } catch (error) {
            console.error('Erro ao carregar dashboard:', error);
        }
    };

    const fetchQueue = async (silent = false) => {
        try {
            if (!silent) setLoading(true);

            const params = {};
            if (selectedDepartment?.value) {
                params.department_id = selectedDepartment.value;
            }

            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/attendances/queue`,
                params,
                true
            );

            if (response.code === 200) {
                const { data } = ApiService.handleApiResponse(response);
                setPendingAttendances(data || []);
            }
        } catch (error) {
            if (!silent) ApiService.handleApiError(error);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const fetchMyActive = async (silent = false) => {
        try {
            // Get attendances I'm currently handling
            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/attendances`,
                { status: 'in_progress', my_attendances: true },
                true
            );

            if (response.code === 200) {
                const { data } = ApiService.handleApiResponse(response);
                setMyActiveAttendances(data || []);
            }
        } catch (error) {
            if (!silent) console.error('Erro ao buscar atendimentos ativos:', error);
        }
    };

    const handleCallNext = async () => {
        try {
            setLoadingPage(true);

            const params = {};
            if (selectedDepartment?.value) {
                params.department_id = selectedDepartment.value;
            }

            const response = await ApiService.send(
                "post",
                `${process.env.REACT_APP_URL_API}video/attendances/queue/call-next`,
                params,
                true
            );

            if (response.code === 200 && response.data) {
                const attendance = response.data;

                // Navigate to the video room to create/start the call
                navigate(`/admin/video/sala/${attendance.id}?host=true`);
            } else {
                Swal.fire('Aviso', 'Não há clientes na fila no momento.', 'info');
            }
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoadingPage(false);
        }
    };

    const handleStartAttendance = async (attendance) => {
        try {
            setLoadingPage(true);

            // Start the attendance
            const response = await ApiService.send(
                "post",
                `${process.env.REACT_APP_URL_API}video/attendances/${attendance.id}/start`,
                {},
                true
            );

            if (response.code === 200) {
                // Navigate to video room
                navigate(`/admin/video/sala/${attendance.id}?host=true`);
            }
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoadingPage(false);
        }
    };

    const handleRejoinCall = async (attendance) => {
        try {
            // Check if there's an active call for this attendance
            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/attendances/${attendance.id}/calls`,
                {},
                true
            );

            if (response.code === 200 && response.data?.length > 0) {
                const activeCall = response.data.find(c => c.status === 'started' || c.status === 'created');
                if (activeCall) {
                    navigate(`/admin/video/sala/${attendance.id}/${activeCall.id}?host=true`);
                    return;
                }
            }

            // No active call, create a new one
            navigate(`/admin/video/sala/${attendance.id}?host=true`);
        } catch (error) {
            ApiService.handleApiError(error);
        }
    };

    const handleCompleteAttendance = async (attendance) => {
        const { value: notes } = await Swal.fire({
            title: 'Finalizar atendimento',
            input: 'textarea',
            inputLabel: 'Observações (opcional)',
            inputPlaceholder: 'Digite suas observações sobre o atendimento...',
            showCancelButton: true,
            confirmButtonText: 'Finalizar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#28a745'
        });

        if (notes === undefined) return; // Cancelled

        try {
            setLoadingPage(true);

            const response = await ApiService.send(
                "post",
                `${process.env.REACT_APP_URL_API}video/attendances/${attendance.id}/complete`,
                { notes },
                true
            );

            if (response.code === 200) {
                Swal.fire('Sucesso', 'Atendimento finalizado!', 'success');
                fetchQueue();
                fetchMyActive();
                fetchDashboard();
            }
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoadingPage(false);
        }
    };

    const handleNoShow = async (attendance) => {
        const result = await Swal.fire({
            title: 'Cliente não compareceu?',
            text: 'Marcar este atendimento como não comparecimento?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sim, não compareceu',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#dc3545'
        });

        if (!result.isConfirmed) return;

        try {
            setLoadingPage(true);

            const response = await ApiService.send(
                "post",
                `${process.env.REACT_APP_URL_API}video/attendances/${attendance.id}/no-show`,
                {},
                true
            );

            if (response.code === 200) {
                Swal.fire('Registrado', 'Não comparecimento registrado.', 'info');
                fetchQueue();
                fetchMyActive();
            }
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoadingPage(false);
        }
    };

    const getWaitingTime = (createdAt) => {
        if (!createdAt) return '-';

        const created = new Date(createdAt);
        const now = new Date();
        const diff = Math.floor((now - created) / 1000);

        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);

        if (hours > 0) {
            return `${hours}h ${minutes}min`;
        }
        return `${minutes}min`;
    };

    const getPriorityBadge = (priority) => {
        const badges = {
            0: null, // Normal - no badge
            1: { class: 'bg-warning', text: 'Alta' },
            2: { class: 'bg-danger', text: 'Urgente' }
        };

        const badge = badges[priority];
        if (!badge) return null;

        return (
            <span className={`badge ${badge.class} ms-2`}>
                {badge.text}
            </span>
        );
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Fila de Atendimento" pageTitle="Vídeo Chamada" />

                    {loadingPage && <LoadingScreen />}

                    {/* Stats Cards */}
                    <Row className="mb-4">
                        <Col md={3}>
                            <div className="video-stats-card">
                                <div className="stat-value text-warning">{dashboardStats.in_queue || 0}</div>
                                <div className="stat-label">Na Fila</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="video-stats-card">
                                <div className="stat-value text-success">{dashboardStats.in_progress || 0}</div>
                                <div className="stat-label">Em Atendimento</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="video-stats-card">
                                <div className="stat-value">{dashboardStats.completed_today || 0}</div>
                                <div className="stat-label">Finalizados Hoje</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="video-stats-card">
                                <div className="stat-value">{dashboardStats.avg_wait_time || 0}min</div>
                                <div className="stat-label">Tempo Médio Espera</div>
                            </div>
                        </Col>
                    </Row>

                    {/* My Active Attendances */}
                    {myActiveAttendances.length > 0 && (
                        <Row className="mb-4">
                            <Col md={12}>
                                <h5 className="mb-3">
                                    <i className="fa fa-video-camera me-2 text-success"></i>
                                    Meus Atendimentos Ativos ({myActiveAttendances.length})
                                </h5>
                                {myActiveAttendances.map(attendance => (
                                    <Card key={attendance.id} className="mb-2 border-success border-2">
                                        <CardBody className="d-flex justify-content-between align-items-center py-3">
                                            <div className="d-flex align-items-center">
                                                <div className="video-user-avatar me-3">
                                                    {attendance.client?.name?.charAt(0)?.toUpperCase() || 'C'}
                                                </div>
                                                <div>
                                                    <h6 className="mb-0">
                                                        {attendance.client?.name || 'Cliente'}
                                                        {getPriorityBadge(attendance.priority)}
                                                    </h6>
                                                    <small className="text-muted">
                                                        {attendance.department?.name || 'Sem departamento'}
                                                    </small>
                                                    <br />
                                                    <small className="text-muted">
                                                        <i className="fa fa-ticket me-1"></i>
                                                        {attendance.protocol}
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-primary btn-sm"
                                                    onClick={() => handleRejoinCall(attendance)}
                                                >
                                                    <i className="fa fa-video-camera me-1"></i> Entrar
                                                </button>
                                                <button
                                                    className="btn btn-success btn-sm"
                                                    onClick={() => handleCompleteAttendance(attendance)}
                                                >
                                                    <i className="fa fa-check me-1"></i> Finalizar
                                                </button>
                                                <button
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={() => handleNoShow(attendance)}
                                                >
                                                    <i className="fa fa-user-times me-1"></i>
                                                </button>
                                            </div>
                                        </CardBody>
                                    </Card>
                                ))}
                            </Col>
                        </Row>
                    )}

                    {/* Department Filter and Call Next */}
                    <Row className="mb-3">
                        <Col md={4}>
                            <div className="form-group">
                                <label>Filtrar por Departamento</label>
                                <Select
                                    options={departments.map(d => ({ value: d.id, label: d.name }))}
                                    value={selectedDepartment}
                                    onChange={setSelectedDepartment}
                                    placeholder="Todos os departamentos"
                                    isClearable
                                />
                            </div>
                        </Col>
                        <Col md={8} className="d-flex justify-content-end align-items-end gap-2">
                            <button
                                className="btn btn-outline-primary"
                                onClick={() => {
                                    fetchQueue();
                                    fetchMyActive();
                                    fetchDashboard();
                                }}
                                disabled={loading}
                            >
                                <i className="fa fa-refresh me-2"></i> Atualizar
                            </button>
                            <button
                                className="btn btn-video-call btn-start"
                                onClick={handleCallNext}
                                disabled={pendingAttendances.length === 0}
                            >
                                <i className="fa fa-phone me-2"></i> Chamar Próximo
                            </button>
                        </Col>
                    </Row>

                    {/* Pending Queue */}
                    <Row>
                        <Col md={12}>
                            <h5 className="mb-3">
                                <i className="fa fa-clock me-2 text-warning"></i>
                                Fila de Espera ({pendingAttendances.length})
                            </h5>

                            {loading ? (
                                <Skeleton count={3} height={80} />
                            ) : pendingAttendances.length > 0 ? (
                                pendingAttendances.map((attendance, index) => (
                                    <div key={attendance.id} className={`video-queue-item ${attendance.priority >= 2 ? 'urgent' : ''}`}>
                                        <Row className="align-items-center">
                                            <Col md={1}>
                                                <div className="text-center">
                                                    <span className="badge bg-primary fs-5">
                                                        {index + 1}
                                                    </span>
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="d-flex align-items-center">
                                                    <div className="video-user-avatar me-3">
                                                        {attendance.client?.name?.charAt(0)?.toUpperCase() || 'C'}
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0">
                                                            {attendance.client?.name || 'Cliente'}
                                                            {getPriorityBadge(attendance.priority)}
                                                        </h6>
                                                        <small className="text-muted">
                                                            {attendance.client?.email || attendance.client?.phone || '-'}
                                                        </small>
                                                        <br />
                                                        <small className="text-muted">
                                                            <i className="fa fa-ticket me-1"></i>
                                                            {attendance.protocol}
                                                        </small>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={2}>
                                                <span className="video-department-badge">
                                                    <i className="fa fa-building me-1"></i>
                                                    {attendance.department?.name || '-'}
                                                </span>
                                            </Col>
                                            <Col md={2}>
                                                <div className="text-center">
                                                    <small className="text-muted d-block">Aguardando</small>
                                                    <span className="video-timer text-warning">
                                                        {getWaitingTime(attendance.queue_entered_at || attendance.created_at)}
                                                    </span>
                                                </div>
                                            </Col>
                                            <Col md={3} className="text-end">
                                                <button
                                                    className="btn btn-video-call btn-start"
                                                    onClick={() => handleStartAttendance(attendance)}
                                                >
                                                    <i className="fa fa-video-camera me-2"></i>
                                                    Atender
                                                </button>
                                            </Col>
                                        </Row>
                                        {attendance.reason && (
                                            <Row className="mt-2">
                                                <Col md={{ size: 11, offset: 1 }}>
                                                    <small className="text-muted">
                                                        <i className="fa fa-comment me-1"></i>
                                                        {attendance.reason}
                                                    </small>
                                                </Col>
                                            </Row>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="video-empty-state">
                                    <i className="fa fa-check-circle text-success"></i>
                                    <h5 className="text-muted">Nenhum cliente aguardando</h5>
                                    <p className="text-muted">
                                        A fila de atendimento está vazia no momento.
                                    </p>
                                </div>
                            )}
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default VideoQueuePage;
